import React, { PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import ToastNotification, { ToastMessage } from '../components/ToastNotification/ToastNotification';
import { FeedbackLevel } from '../interfaces/FeedbackLevel';

export interface INotificationContext {
    toastMessage?: ToastMessage;
    success: (message: string | string[]) => void;
    error: (message: string | string[]) => void;
    notify: (level: FeedbackLevel, message: string | string[]) => void;
}

export const NotificationContext = React.createContext<INotificationContext | undefined>(undefined);
NotificationContext.displayName = 'NotificationContext';

export function useNotificationContext() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotificationContext must be used within NotificationContextProvider');
    }
    return context;
}

export const NotificationProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [toastMessage, setToastMessage] = React.useState<ToastMessage>();
    const idRef = React.useRef(0);

    const setToast = useCallback((level: FeedbackLevel, message: string | string[]) => {
        idRef.current += 1;
        setToastMessage({
            message,
            level,
            id: idRef.current,
            onClose: removeMessage,
        });
    }, []);

    const removeMessage = useCallback((id: number) => {
        setToastMessage(undefined);
    }, []);

    const success = useCallback(
        (message: string | string[]) => {
            setToast('info', message);
        },
        [setToast]
    );

    const error = useCallback(
        (message: string | string[] = 'An error occurred') => {
            setToast('danger', message);
        },
        [setToast]
    );

    const notify = useCallback(
        (level: FeedbackLevel, message: string | string[]) => {
            setToast(level, message);
        },
        [setToast]
    );

    const value = useMemo(
        () => ({ toastMessage, success, error, notify, removeMessage }),
        [toastMessage, success, error, notify, removeMessage]
    );

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {toastMessage && <ToastNotification {...toastMessage} />}
        </NotificationContext.Provider>
    );
};
